/**
 * Demandes de formation des agents, côté RH — M27 (retour Manon, mi-parcours).
 *
 * Les souhaits déposés dans « Mes demandes » arrivent ici (et par cloche). La RH
 * accepte ou refuse ; un refus se motive. L'agent reçoit la réponse par cloche.
 * Masquée s'il n'y a rien : pas de carte vide dans l'onglet.
 */
import { useCallback, useEffect, useState } from 'react';
import { Card, Badge, Button, Input } from '@/components/ui';
import { api, ApiException, type DemandeFormationDTO } from '@/lib/api';

export function DemandesFormationRhCard() {
  const [items, setItems] = useState<DemandeFormationDTO[]>([]);
  const [reponses, setReponses] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const [voirTraitees, setVoirTraitees] = useState(false);

  const charger = useCallback(async () => {
    try { setItems(await api.rh.demandesFormation()); }
    catch { setItems([]); }
  }, []);
  useEffect(() => { void charger(); }, [charger]);

  async function statuer(id: string, statut: 'acceptee' | 'refusee') {
    setErr(null);
    try {
      await api.rh.statuerDemandeFormation(id, { statut, reponse: reponses[id]?.trim() || null });
      await charger();
    } catch (e) {
      setErr(e instanceof ApiException ? e.payload.error : 'Décision impossible');
    }
  }

  const enAttente = items.filter((i) => i.statut === 'en_attente');
  const traitees = items.filter((i) => i.statut === 'acceptee' || i.statut === 'refusee');
  if (items.length === 0) return null;
  const fr = (d: string) => new Date(d).toLocaleDateString('fr-FR');

  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center gap-2">
        <h2 className="font-display text-base font-semibold text-ink">Demandes de formation des agents</h2>
        {enAttente.length > 0 && <Badge variant="urgent">{enAttente.length} à traiter</Badge>}
      </div>
      {err && <p className="mb-2 text-xs text-urgent">{err}</p>}
      {enAttente.length === 0 && <p className="text-sm text-ink-subtle">Aucune demande en attente.</p>}
      <ul className="space-y-2">
        {enAttente.map((d) => (
          <li key={d.id} className="rounded-r-md border border-border/60 p-3 text-sm">
            <p className="text-ink"><b>{d.agentNom ?? 'Agent'}</b> — {d.intitule}{d.organisme ? ` (${d.organisme})` : ''}</p>
            {(d.periodeDu || d.periodeAu) && (
              <p className="text-xs text-ink-muted">Souhaitée {d.periodeDu ? `à partir du ${fr(d.periodeDu)}` : ''}{d.periodeAu ? ` jusqu'au ${fr(d.periodeAu)}` : ''}</p>
            )}
            {d.commentaire && <p className="text-xs text-ink-muted">« {d.commentaire} »</p>}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Input className="min-w-[220px] flex-1" value={reponses[d.id] ?? ''} maxLength={2000}
                placeholder="Réponse (obligatoire pour un refus)" aria-label="Réponse à l'agent"
                onChange={(e) => setReponses((r) => ({ ...r, [d.id]: e.target.value }))} />
              <Button size="sm" variant="rh" onClick={() => void statuer(d.id, 'acceptee')}>Accepter</Button>
              <Button size="sm" variant="ghost" onClick={() => void statuer(d.id, 'refusee')}>Refuser</Button>
            </div>
          </li>
        ))}
      </ul>
      {traitees.length > 0 && (
        <div className="mt-3">
          <button type="button" className="text-xs text-accent hover:underline" onClick={() => setVoirTraitees((v) => !v)}>
            {voirTraitees ? 'Masquer' : 'Voir'} les {traitees.length} demande(s) traitée(s)
          </button>
          {voirTraitees && (
            <ul className="mt-2 space-y-1">
              {traitees.map((d) => (
                <li key={d.id} className="text-xs text-ink-muted">
                  {d.agentNom} — {d.intitule} · <b>{d.statut === 'acceptee' ? 'acceptée' : 'refusée'}</b>
                  {d.traiteParNom ? ` par ${d.traiteParNom}` : ''}{d.reponse ? ` — ${d.reponse}` : ''}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}
